// src/pages/PatientHistory.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";

// ---------- Helper functions ----------
const pad = (n) => String(n).padStart(2, "0");

function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]), month = Number(m[2]) - 1, day = Number(m[3]);
  const hour = Number(m[4] ?? 0), minute = Number(m[5] ?? 0), second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}

function formatDateTime12(s) {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mon = months[dt.getMonth()];
  const year = dt.getFullYear();
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  hr = hr % 12 || 12;
  const ampm = dt.getHours() >= 12 ? "PM" : "AM";
  return `${day}-${mon}-${year} ${pad(hr)}:${min} ${ampm}`;
}

const money = (v) => `₹${Number(v || 0).toFixed(2)}`;

// ---------- Main Component ----------
export default function PatientHistory() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const hasAutoSearched = useRef(false);

  // Data states
  const [labRecords, setLabRecords] = useState([]);
  const [opdRecords, setOpdRecords] = useState([]);
  const [ipdRecords, setIpdRecords] = useState([]);
  const [pharmacyRecords, setPharmacyRecords] = useState([]);
  const [otRecords, setOtRecords] = useState([]);

  // Reference data for lookups
  const [caseList, setCaseList] = useState([]);

  // Totals object returned by backend
  const [totals, setTotals] = useState({
    lab: { amount: 0, paid: 0, due: 0 },
    opd: { amount: 0, paid: 0, due: 0 },
    ipd: { amount: 0, paid: 0, due: 0 },
    ot: { amount: 0, paid: 0, due: 0 },
    pharmacy: { amount: 0, paid: 0, due: 0 }
  });

  // Tab state (default lab first)
  const [activeTab, setActiveTab] = useState("lab");

  // ---------- Load case list on mount ----------
  useEffect(() => {
    loadCaseList();
  }, []);

  const loadCaseList = async () => {
    try {
      const storedData = sessionStorage.getItem("ldata");
      if (!storedData) return;

      const parsed = JSON.parse(storedData);
      const fd = new FormData();
      fd.append('cat_type', 'case');
      fd.append('page_no', 'none');
      fd.append('search_key', '');
      fd.append('par_by', parsed.user_id || 1);

      const response = await axios.post(
        AppConst.BASE_URL + AppConst.getCatListUrl,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status) {
        setCaseList(response.data.records || []);
        console.log("✅ Case list loaded:", response.data.records?.length || 0);
      }
    } catch (err) {
      console.error("Failed to load case list:", err);
    }
  };

  // Helper function to get case name by ID
  const getCaseNameById = (caseId) => {
    if (!caseId) return "";
    const caseItem = caseList.find(c => String(c.cat_id) === String(caseId));
    return caseItem?.cat_name || "";
  };

  // ---------- Load prefilled data from sessionStorage ----------
  useEffect(() => {
    if (hasAutoSearched.current) return;

    const raw = sessionStorage.getItem("history_prefill");
    if (!raw) return;

    try {
      const prefill = JSON.parse(raw);
      console.log("🔍 History prefill loaded:", prefill);

      // Extract UHID from various possible fields
      const uhid = prefill.ipd_uhid || 
                   prefill.uhid || 
                   prefill.opd_uhid || 
                   prefill.lab_reg_uhid || 
                   "";

      if (uhid && uhid.trim()) {
        setQuery(uhid.trim());
        
        // Auto-search after a small delay to ensure UI is ready
        setTimeout(() => {
          handleSearchWithQuery(uhid.trim());
        }, 300);

        hasAutoSearched.current = true;
      }

      // Optionally set patient info if available in prefill
      if (prefill.ipd_patient_name || prefill.name) {
        setPatientInfo({
          name: prefill.ipd_patient_name || prefill.name || "",
          mobile: prefill.ipd_patient_mobile || prefill.mobile || "",
          uhid: uhid
        });
      }

      // Clean up sessionStorage after loading
      sessionStorage.removeItem("history_prefill");
    } catch (err) {
      console.error("Error loading history prefill:", err);
    }
  }, []);

  // ---------- Search handler ----------
  const handleSearch = async () => {
    if (!query || query.trim() === "") {
      Swal.fire("Required", "Please enter UHID / Mobile / Name to search", "warning");
      return;
    }
    await handleSearchWithQuery(query.trim());
  };

  const handleSearchWithQuery = async (searchQuery) => {
    if (!searchQuery || searchQuery.trim() === "") {
      Swal.fire("Required", "Please enter UHID / Mobile / Name to search", "warning");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("query", searchQuery);
      fd.append("page_no", "none");

      const url = AppConst.BASE_URL + (AppConst.getPatientHistoryUrl);
      const res = await axios.post(url, fd);

      if (res?.data?.status) {
        const data = res.data;

        // Set patient info
        setPatientInfo(data.patient_info || null);

        // Set records in the requested order: lab, opd, ipd, ot, pharmacy
        setLabRecords(data.lab_records || []);
        setOpdRecords(data.opd_records || []);
        setIpdRecords(data.ipd_records || []);
        
        // Process OT records to add case names
        const processedOtRecords = (data.ot_records || []).map(record => ({
          ...record,
          ot_case_name: record.ot_case_name || getCaseNameById(record.ot_case) || record.ot_case || ""
        }));
        setOtRecords(processedOtRecords);
        
        setPharmacyRecords(data.pharmacy_records || []);

        // Set totals if provided
        if (data.totals) {
          setTotals((prev) => ({ ...prev, ...data.totals }));
        } else if (data.summary && data.summary.totals) {
          setTotals((prev) => ({ ...prev, ...data.summary.totals }));
        } else {
          setTotals({
            lab: { amount: 0, paid: 0, due: 0 },
            opd: { amount: 0, paid: 0, due: 0 },
            ipd: { amount: 0, paid: 0, due: 0 },
            ot: { amount: 0, paid: 0, due: 0 },
            pharmacy: { amount: 0, paid: 0, due: 0 }
          });
        }

        // Auto-select first tab with data (using requested order)
        if ((data.lab_records || []).length > 0) setActiveTab("lab");
        else if ((data.opd_records || []).length > 0) setActiveTab("opd");
        else if ((data.ipd_records || []).length > 0) setActiveTab("ipd");
        else if (processedOtRecords.length > 0) setActiveTab("ot");
        else if ((data.pharmacy_records || []).length > 0) setActiveTab("pharmacy");
        else {
          Swal.fire("No Records", "No records found for this query", "info");
        }
      } else {
        Swal.fire("Error", res.data?.message || "Failed to fetch patient history", "error");
        clearResults();
      }
    } catch (err) {
      console.error("Search error:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to fetch patient history", "error");
      clearResults();
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setPatientInfo(null);
    setLabRecords([]);
    setOpdRecords([]);
    setIpdRecords([]);
    setPharmacyRecords([]);
    setOtRecords([]);
    setTotals({
      lab: { amount: 0, paid: 0, due: 0 },
      opd: { amount: 0, paid: 0, due: 0 },
      ipd: { amount: 0, paid: 0, due: 0 },
      ot: { amount: 0, paid: 0, due: 0 },
      pharmacy: { amount: 0, paid: 0, due: 0 }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClear = () => {
    setQuery("");
    clearResults();
    hasAutoSearched.current = false;
  };

  // ---------- Table columns ----------
  const labColumns = [
    { name: "SL", selector: (r, i) => i + 1, width: "60px" },
    { name: "Lab Reg ID", selector: r => r.lab_reg_id || "", width: "110px" },
    { name: "UHID", selector: r => r.lab_reg_uhid || "", width: "120px" },
    { name: "Patient Name", selector: r => r.lab_reg_name || "", wrap: true, width: "180px" },
    { name: "Mobile", selector: r => r.lab_reg_mobile || "", width: "130px" },
    { name: "Tests", selector: r => r.displayTestNames || "", wrap: true, width: "250px" },
    { name: "Date", selector: r => r.lab_reg_datetime || "", cell: r => formatDateTime12(r.lab_reg_datetime), width: "160px" },
    { name: "Amount", selector: r => r.lab_final_amt || 0, cell: r => money(r.lab_final_amt || 0), right: true, width: "100px" },
    { name: "Paid", selector: r => r.lab_paid || 0, cell: r => money(r.lab_paid || 0), right: true, width: "100px" },
    { name: "Due", selector: r => r.lab_due || 0, cell: r => money(r.lab_due || 0), right: true, width: "100px" }
  ];

  const opdColumns = [
    { name: "SL", selector: (r, i) => i + 1, width: "60px" },
    { name: "OPD ID", selector: r => r.opd_id || "", width: "100px" },
    { name: "UHID", selector: r => r.opd_uhid || "", width: "120px" },
    { name: "Patient Name", selector: r => r.opd_patient_name || "", wrap: true, width: "180px" },
    { name: "Mobile", selector: r => r.opd_patient_mobile || "", width: "130px" },
    { name: "Doctor", selector: r => r.opd_cons_doc_name || r.opd_ref_doc_name || "", wrap: true, width: "160px" },
    { name: "Date", selector: r => r.opd_datetime || "", cell: r => formatDateTime12(r.opd_datetime), width: "160px" },
    { name: "Amount", selector: r => r.opd_final_amt || 0, cell: r => money(r.opd_final_amt || 0), right: true, width: "100px" },
    { name: "Status", selector: r => r.status === "0" ? "Active" : r.status === "1" ? "Closed" : r.status, width: "100px" }
  ];

  const ipdColumns = [
    { name: "SL", selector: (r, i) => i + 1, width: "60px" },
    { name: "IPD No", selector: r => `000${r.ipd_id || ""}`, width: "110px" },
    { name: "UHID", selector: r => r.ipd_uhid || "", width: "120px" },
    { name: "Patient Name", selector: r => r.ipd_patient_name || "", wrap: true, width: "180px" },
    { name: "Mobile", selector: r => r.ipd_patient_mobile || "", width: "130px" },
    { name: "Ward/Bed", selector: r => `${r.ipd_ward_name || ""} / ${r.ipd_bed_name || ""}`, wrap: true, width: "160px" },
    { name: "Admission", selector: r => r.ipd_datetime || "", cell: r => formatDateTime12(r.ipd_datetime), width: "160px" },
    { name: "Discharge", selector: r => r.ipd_discharge_date || "", cell: r => formatDateTime12(r.ipd_discharge_date), width: "160px" },
    { name: "Amount", selector: r => r.ipd_total_paid || 0, cell: r => money(r.ipd_total_paid || 0), right: true, width: "110px" },
    { name: "Status", selector: r => r.status === "0" ? "Active" : r.status === "1" ? "Discharged" : r.status, width: "100px" }
  ];

  const pharmacyColumns = [
    { name: "SL", selector: (r, i) => i + 1, width: "60px" },
    { name: "Bill ID", selector: r => r.med_bill_id || "", width: "100px" },
    { name: "Patient Number", selector: r => r.med_bill_patient_number || "", width: "140px" },
    { name: "IPD Number", selector: r => r.med_bill_ipd_number || "", width: "120px" },
    { name: "Phone", selector: r => r.med_bill_phone_number || "", width: "130px" },
    { name: "Type", selector: r => r.med_bill_type || "", width: "100px" },
    { name: "Date", selector: r => r.med_bill_date || "", cell: r => formatDateTime12(r.med_bill_date), width: "160px" },
    { name: "Amount", selector: r => r.med_bill_total_final || 0, cell: r => money(r.med_bill_total_final || 0), right: true, width: "110px" },
    { name: "Paid", selector: r => r.med_bill_paid || 0, cell: r => money(r.med_bill_paid || 0), right: true, width: "100px" },
    { name: "Due", selector: r => r.med_bill_due || 0, cell: r => money(r.med_bill_due || 0), right: true, width: "100px" }
  ];

  const otColumns = [
    { name: "SL", selector: (r, i) => i + 1, width: "60px" },
    { name: "OT ID", selector: r => r.ot_id || "", width: "100px" },
    { name: "IPD No", selector: r => r.ot_ipd_no || "", width: "110px" },
    { name: "Patient Name", selector: r => r.ot_patient_name || "", wrap: true, width: "180px" },
    { name: "OT Number", selector: r => r.ot_number || "", width: "120px" },
    { name: "Doctor", selector: r => r.ot_doctor_name || r.ot_doctor || "", wrap: true, width: "160px" },
    { name: "Anesthesia", selector: r => r.ot_anesthesia_doctor_name || r.ot_anesthesia_doctor || "", wrap: true, width: "160px" },
    { name: "Date/Time", selector: r => r.ot_datetime || "", cell: r => formatDateTime12(r.ot_datetime), width: "160px" },
    { 
      name: "Case", 
      selector: r => r.ot_case_name || r.ot_case || "", 
      wrap: true, 
      width: "150px",
      cell: r => r.ot_case_name || <span className="text-muted">{r.ot_case || "N/A"}</span>
    }
  ];

  const defaultStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color: '#405189', fontSize: '14px', fontWeight: 700 } },
    rows: { style: { minHeight: '52px' } }
  };

  // Replace the renderFooterTotals function with this:

const renderFooterTotals = (section) => {
  const t = totals[section] || { amount: 0, paid: 0, due: 0 };
  
  // IPD: Show only Total Paid
  if (section === 'ipd') {
    return (
      <div className="mt-2 p-2 border rounded bg-light">
        <div className="row text-muted">
          <div className="col-auto"><strong>Totals:</strong></div>
          <div className="col">Total Paid: <strong>{money(t.paid)}</strong></div>
        </div>
      </div>
    );
  }
  
  // OT: Show nothing (no footer)
  if (section === 'ot') {
    return null;
  }
  
  // Pharmacy: Show only Total Paid
  if (section === 'pharmacy') {
    return (
      <div className="mt-2 p-2 border rounded bg-light">
        <div className="row text-muted">
          <div className="col-auto"><strong>Totals:</strong></div>
          <div className="col">Total Paid: <strong>{money(t.paid)}</strong></div>
        </div>
      </div>
    );
  }
  
  // Lab and OPD: Show all (Amount, Paid, Due)
  return (
    <div className="mt-2 p-2 border rounded bg-light">
      <div className="row text-muted">
        <div className="col-auto"><strong>Totals:</strong></div>
        <div className="col">Total Amount: <strong>{money(t.amount)}</strong></div>
        <div className="col">Total Paid: <strong>{money(t.paid)}</strong></div>
        <div className="col">Total Due: <strong>{money(t.due)}</strong></div>
      </div>
    </div>
  );
};

  // ---------- Render ----------
  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Header Card */}
        <div className="card mb-3">
          <div className="card-body">
            <h4 className="card-title mb-3">Patient History</h4>
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Search by UHID / Mobile / Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter UHID, Mobile or Name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="ri-search-line me-1"></i>
                      Search
                    </>
                  )}
                </button>
              </div>
              <div className="col-md-1">
                <button
                  className="btn btn-secondary w-100"
                  onClick={handleClear}
                  disabled={loading}
                >
                  <i className="ri-refresh-line"></i>
                </button>
              </div>
              {patientInfo && (
                <div className="col-md-5">
                  <div className="alert alert-info mb-0 py-2">
                    <strong>Patient:</strong> {patientInfo.name || "N/A"} &nbsp;|&nbsp;
                    <strong>Mobile:</strong> {patientInfo.mobile || "N/A"} &nbsp;|&nbsp;
                    <strong>UHID:</strong> {patientInfo.uhid || query}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results Card */}
        {(labRecords.length > 0 || opdRecords.length > 0 || ipdRecords.length > 0 || pharmacyRecords.length > 0 || otRecords.length > 0) && (
          <div className="card">
            <div className="card-body">
              {/* Tab Navigation */}
              <ul className="nav nav-tabs mb-3" role="tablist">
                {labRecords.length > 0 && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "lab" ? "active" : ""}`}
                      onClick={() => setActiveTab("lab")}
                    >
                      Lab ({labRecords.length})
                    </button>
                  </li>
                )}
                {opdRecords.length > 0 && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "opd" ? "active" : ""}`}
                      onClick={() => setActiveTab("opd")}
                    >
                      OPD ({opdRecords.length})
                    </button>
                  </li>
                )}
                {ipdRecords.length > 0 && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "ipd" ? "active" : ""}`}
                      onClick={() => setActiveTab("ipd")}
                    >
                      IPD ({ipdRecords.length})
                    </button>
                  </li>
                )}
                {otRecords.length > 0 && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "ot" ? "active" : ""}`}
                      onClick={() => setActiveTab("ot")}
                    >
                      OT ({otRecords.length})
                    </button>
                  </li>
                )}
                {pharmacyRecords.length > 0 && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "pharmacy" ? "active" : ""}`}
                      onClick={() => setActiveTab("pharmacy")}
                    >
                      Pharmacy ({pharmacyRecords.length})
                    </button>
                  </li>
                )}
              </ul>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === "lab" && labRecords.length > 0 && (
                  <>
                    <DataTable
                      columns={labColumns}
                      data={labRecords}
                      pagination
                      highlightOnHover
                      responsive
                      customStyles={defaultStyles}
                    />
                    {renderFooterTotals('lab')}
                  </>
                )}

                {activeTab === "opd" && opdRecords.length > 0 && (
                  <>
                    <DataTable
                      columns={opdColumns}
                      data={opdRecords}
                      pagination
                      highlightOnHover
                      responsive
                      customStyles={defaultStyles}
                    />
                    {renderFooterTotals('opd')}
                  </>
                )}

                {activeTab === "ipd" && ipdRecords.length > 0 && (
                  <>
                    <DataTable
                      columns={ipdColumns}
                      data={ipdRecords}
                      pagination
                      highlightOnHover
                      responsive
                      customStyles={defaultStyles}
                    />
                    {renderFooterTotals('ipd')}
                  </>
                )}

                {activeTab === "ot" && otRecords.length > 0 && (
                  <>
                    <DataTable
                      columns={otColumns}
                      data={otRecords}
                      pagination
                      highlightOnHover
                      responsive
                      customStyles={defaultStyles}
                    />
                    {renderFooterTotals('ot')}
                  </>
                )}

                {activeTab === "pharmacy" && pharmacyRecords.length > 0 && (
                  <>
                    <DataTable
                      columns={pharmacyColumns}
                      data={pharmacyRecords}
                      pagination
                      highlightOnHover
                      responsive
                      customStyles={defaultStyles}
                    />
                    {renderFooterTotals('pharmacy')}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}